import { useCallback, useEffect, useRef, useState } from 'react';

import { type AuthError, useSafeAuth } from '@opendoor/auth-fe/context';
import { GENERIC_AUTH_FAILURE_MESSAGE, isExpectedLoginFailure } from '@opendoor/auth-fe/errors';
import { analytics } from '@opendoor/observability/slim';

import type { IRequestStatus } from './RequestStatus';

// Expected login failures map to the HTTP 401 the page's Sentry gate keys on;
// everything else stays capturable.
const UNAUTHORIZED_STATUS_CODE = 401;

const IDLE_STATUS: IRequestStatus = { isLoading: false };
const LOADING_STATUS: IRequestStatus = { isLoading: true };

// Rebuilds the user-facing copy the legacy session saga derived from Devise's
// field-error object (`That <field> <message>.`), which sessionClient now hands
// back as `fieldErrors` with an empty top-level message.
function formatFieldErrors(fieldErrors: Record<string, string[]> | undefined): string {
  if (!fieldErrors) {
    return '';
  }
  const [field] = Object.keys(fieldErrors);
  const first = field ? fieldErrors[field]?.[0] : undefined;
  if (!field || !first) {
    return '';
  }
  if (field === 'base') {
    return first;
  }
  const label = field.replace('primary_phone', 'phone number').replace('_', ' ');
  return `That ${label} ${first}.`;
}

function registrationErrorMessage(error: AuthError): string {
  return error.message || formatFieldErrors(error.fieldErrors) || GENERIC_AUTH_FAILURE_MESSAGE;
}

/**
 * The slice of the session surface `AccountFormPage` consumes, held on the
 * `useAuth` write-path: the auth-fe provider owns the session read, and the
 * write-path methods keep their loading/error/result state local so the page
 * chrome (`AccountMagicLinkGate`, post-login redirects, SMS-consent timing)
 * keeps its established behavior. The V2 modules own their write path
 * internally and receive none of these callbacks.
 */
export interface AccountFormSession {
  loggedIn: boolean;
  infoIsLoaded: boolean;
  sessionEmail: string | undefined;
  serverLoginRedirect: string | undefined;
  email: string;
  isNewHuman: boolean;
  hasGeneratedPassword: boolean | undefined;
  loginRequestStatus: IRequestStatus;
  humanStatusRequestStatus: IRequestStatus;
  registrationRequestStatus: IRequestStatus;
  logIn: (email?: string, password?: string, googleIDToken?: string) => Promise<void>;
  register: (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
  ) => Promise<void>;
  requestHumanStatus: (email: string) => Promise<void>;
  requestResetPassword: (email: string) => void;
  maybeFetchSessionInfo: () => void;
}

/**
 * Backs `AccountFormPage` with the `useAuth` write-path. The session read
 * (`loggedIn`/`sessionEmail`/`infoIsLoaded`) comes from the auth-fe provider's
 * own session query; the write-path methods hold their loading/error/result
 * state locally, so the page's redirect, SMS-consent timing, and magic-link
 * gate are unchanged.
 *
 * The login/register/human-status/reset journey events fire here rather than in
 * the form: the form's own modules own their write path and emit none of them.
 */
export function useAccountFormSession(): AccountFormSession {
  // useSafeAuth: mounting this bridge without an AuthContextProvider is a
  // wiring bug — the lenient useAuth fallback would render a form whose every
  // submit silently fails with generic copy.
  const {
    authentication,
    user,
    logIn: authLogIn,
    register: authRegister,
    requestHumanStatus: authRequestHumanStatus,
    requestPasswordReset: authRequestPasswordReset,
    refresh,
  } = useSafeAuth();

  const [writeLoggedIn, setWriteLoggedIn] = useState(false);
  const [serverLoginRedirect, setServerLoginRedirect] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [isNewHuman, setIsNewHuman] = useState(false);
  // undefined until the first human-status resolution sets a real boolean —
  // the magic-link gate treats a boolean as "status resolved", so starting
  // false would collapse its unresolved/loading state (legacy Redux started
  // this undefined).
  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean | undefined>(undefined);
  const [loginRequestStatus, setLoginRequestStatus] = useState<IRequestStatus>(IDLE_STATUS);
  const [humanStatusRequestStatus, setHumanStatusRequestStatus] =
    useState<IRequestStatus>(IDLE_STATUS);
  const [registrationRequestStatus, setRegistrationRequestStatus] =
    useState<IRequestStatus>(IDLE_STATUS);

  // The write-path optimistically latches logged-in so the post-write redirect
  // fires without waiting on a session re-read. Clear it only on a real
  // authenticated -> not_authenticated transition (e.g. session expiry via
  // refresh()), so loggedIn follows the provider down the way the Redux value
  // did — while never firing in the window between a write success and the
  // provider catching up (where the state is still not_authenticated but was
  // never authenticated), which would drop the redirect.
  const sawProviderAuthenticated = useRef(false);
  useEffect(() => {
    if (authentication.state === 'authenticated') {
      sawProviderAuthenticated.current = true;
    } else if (authentication.state === 'not_authenticated' && sawProviderAuthenticated.current) {
      sawProviderAuthenticated.current = false;
      setWriteLoggedIn(false);
    }
  }, [authentication.state]);

  const loggedIn = writeLoggedIn || authentication.state === 'authenticated';
  // refresh() keeps reporting the pre-write not_authenticated state until the
  // session re-read lands (it never passes through 'authenticating'), so treat
  // that window as info-not-loaded: consumers that need the hydrated session —
  // the magic-link gate reads `sessionEmail` for the account email — show their
  // loading state instead of a missing-session dead end.
  const providerCatchingUp = writeLoggedIn && authentication.state === 'not_authenticated';
  const infoIsLoaded = authentication.state !== 'authenticating' && !providerCatchingUp;

  const sessionEmail = user?.email;

  const logIn = useCallback<AccountFormSession['logIn']>(
    async (loginEmail, password, googleIDToken) => {
      setLoginRequestStatus(LOADING_STATUS);
      const result = await authLogIn(
        googleIDToken
          ? { googleIdToken: googleIDToken }
          : { email: loginEmail ?? '', password: password ?? '' },
      );
      if (result.success) {
        setServerLoginRedirect(result.data.redirectTo);
        setWriteLoggedIn(true);
        // The write does not update the provider's session read: without a
        // refresh, `user`/`human` stay empty until a full page load and the
        // magic-link gate mounts with no account email.
        refresh();
        setLoginRequestStatus({ isLoading: false, success: true });
        analytics.trackEvent('session', 'login', 'success', { email: loginEmail });
        return;
      }
      setLoginRequestStatus({
        isLoading: false,
        error: result.error.message || GENERIC_AUTH_FAILURE_MESSAGE,
        statusCode: isExpectedLoginFailure(result.error.code)
          ? UNAUTHORIZED_STATUS_CODE
          : undefined,
      });
      analytics.trackEvent('session', 'login', 'error', {
        email: loginEmail,
        error: result.error.message,
      });
    },
    [authLogIn, refresh],
  );

  const register = useCallback<AccountFormSession['register']>(
    async (registerEmail, name, password, phone, googleIDToken) => {
      setRegistrationRequestStatus(LOADING_STATUS);
      const result = await authRegister({
        email: registerEmail ?? '',
        fullName: name ?? '',
        primaryPhone: phone ?? '',
        ...(googleIDToken ? { googleIdToken: googleIDToken } : { password: password ?? '' }),
      });
      if (result.success) {
        setServerLoginRedirect(result.data.redirectTo);
        setWriteLoggedIn(true);
        refresh();
        setRegistrationRequestStatus({ isLoading: false, success: true });
        analytics.trackEvent('session', 'register', 'success', { email: registerEmail });
        return;
      }
      setRegistrationRequestStatus({
        isLoading: false,
        error: registrationErrorMessage(result.error),
      });
      analytics.trackEvent('session', 'register', 'error', {
        email: registerEmail,
        error: result.error.message,
      });
    },
    [authRegister, refresh],
  );

  const requestHumanStatus = useCallback<AccountFormSession['requestHumanStatus']>(
    async (statusEmail) => {
      setHumanStatusRequestStatus(LOADING_STATUS);
      const result = await authRequestHumanStatus(statusEmail);
      if (result.success) {
        // Publish email alongside the resolved status the way the legacy saga's
        // successRequestHumanStatus did, so the form never sees the new email
        // paired with a stale isNewHuman/hasGeneratedPassword.
        setEmail(statusEmail);
        setIsNewHuman(result.data.isNewHuman);
        setHasGeneratedPassword(result.data.hasGeneratedPassword);
        setHumanStatusRequestStatus({ isLoading: false, success: true });
        analytics.trackEvent(
          'session',
          'request-human-status',
          result.data.isNewHuman ? 'no-account' : 'success',
          {
            email: statusEmail,
            hasAutoGeneratedPassword: result.data.hasGeneratedPassword,
            isNewHuman: result.data.isNewHuman,
          },
        );
        return;
      }
      setHumanStatusRequestStatus({
        isLoading: false,
        error: result.error.message || GENERIC_AUTH_FAILURE_MESSAGE,
      });
      analytics.trackEvent('session', 'request-human-status', 'error', {
        email: statusEmail,
        error: result.error.message,
      });
    },
    [authRequestHumanStatus],
  );

  const requestResetPassword = useCallback<AccountFormSession['requestResetPassword']>(
    (resetEmail) => {
      void (async () => {
        const result = await authRequestPasswordReset(resetEmail);
        if (result.success) {
          const payload = {
            email: resetEmail,
            hasAutoGeneratedPassword: result.data.hasGeneratedPassword,
            isNewHuman: result.data.isNewHuman,
          };
          // An existing account (not new) means a reset/setup email was actually
          // sent; the saga's maybeResetPassword emitted this extra "email sent"
          // signal (with shouldResetPassword) before the success/no-account event.
          if (!result.data.isNewHuman) {
            analytics.trackEvent('session', 'reset-password', 'reset-password', {
              ...payload,
              shouldResetPassword: true,
            });
          }
          analytics.trackEvent(
            'session',
            'reset-password',
            result.data.isNewHuman ? 'no-account' : 'success',
            payload,
          );
          return;
        }
        analytics.trackEvent('session', 'reset-password', 'error', {
          email: resetEmail,
          error: result.error.message,
        });
      })();
    },
    [authRequestPasswordReset],
  );

  // The auth-fe provider issues the session query on mount, so the legacy
  // "fetch session info if not already loaded" call has nothing left to do.
  const maybeFetchSessionInfo = useCallback(() => {}, []);

  return {
    loggedIn,
    infoIsLoaded,
    sessionEmail,
    serverLoginRedirect,
    email,
    isNewHuman,
    hasGeneratedPassword,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    logIn,
    register,
    requestHumanStatus,
    requestResetPassword,
    maybeFetchSessionInfo,
  };
}
